<script lang="ts" setup>
import { useI18n } from "vue-i18n";
import { useTruck } from "@/composables";
import TruckTrackingStatus from "@/components/statuses/TruckTrackingStatus.vue";
import type { TruckDto } from "@/api/trucks/dto/truck.dto";

defineProps<{
  truck: TruckDto;
}>();

const { t } = useI18n();
const { doorTypeToString, equipmentToString, additionalEquipmentToString } = useTruck();
</script>

<template>
  <div class="h-100">
    <div class="box-shadow pa-6 bg-white rounded mb-6">
      <div class="text-grey-darken-3 mb-6 font-weight-medium text-subtitle-1">{{ t("trucks.characteristics") }}</div>
      <div class="characteristics-items">
        <div class="d-flex characteristics-item">
          <span class="text-grey-darken-1 characteristics-label">{{ t("trucks.make") }}</span>
          <span class="text-grey-darken-3" data-qa="truck-make">{{ truck.make }}</span>
        </div>
        <div class="d-flex characteristics-item">
          <span class="text-grey-darken-1 characteristics-label">{{ t("trucks.model") }}</span>
          <span class="text-grey-darken-3" data-qa="truck-model">{{ truck.model }}</span>
        </div>
        <div class="d-flex characteristics-item">
          <span class="text-grey-darken-1 characteristics-label">{{ t("trucks.gross-weight") }}</span>
          <span class="text-grey-darken-3" data-qa="truck-gross-weight">{{ truck.grossWeight }}</span>
        </div>
        <div class="d-flex characteristics-item">
          <span class="text-grey-darken-1 characteristics-label">{{ t("trucks.vin") }}</span>
          <span class="text-grey-darken-3" data-qa="truck-vin-code">{{ truck.vin }}</span>
        </div>
        <div class="d-flex characteristics-item">
          <span class="text-grey-darken-1 characteristics-label">{{ t("trucks.year") }}</span>
          <span class="text-grey-darken-3" data-qa="truck-year">{{ truck.year }}</span>
        </div>
        <div class="d-flex characteristics-item">
          <span class="text-grey-darken-1 characteristics-label">{{ t("trucks.external-tracking") }}</span>
          <truck-tracking-status :enable-tracking="truck.enableTracking" />
        </div>
        <div class="d-flex characteristics-item">
          <span class="text-grey-darken-1 characteristics-label">{{ t("trucks.license-plate") }}</span>
          <span class="text-grey-darken-3" data-qa="truck-licence-plate">{{ truck.licencePlate }}</span>
        </div>
        <div class="d-flex characteristics-item">
          <span class="text-grey-darken-1 characteristics-label">{{ t("trucks.license-state") }}</span>
          <span class="text-grey-darken-3" data-qa="truck-licence-state">{{ truck.licenceState }}</span>
        </div>
        <div class="d-flex characteristics-item">
          <span class="text-grey-darken-1 characteristics-label">{{ t("trucks.license-expire") }}</span>
          <span class="text-grey-darken-3" data-qa="truck-licence-expire">{{
            $filters.date(truck.licenceExpire)
          }}</span>
        </div>
        <div class="d-flex characteristics-item">
          <span class="text-grey-darken-1 characteristics-label">{{ t("trucks.color") }}</span>
          <span class="text-grey-darken-3" data-qa="truck-color">{{ truck.color }}</span>
        </div>
        <div class="d-flex characteristics-item">
          <span class="text-grey-darken-1 characteristics-label">{{ t("trucks.signs") }}</span>
          <span class="text-grey-darken-3" data-qa="truck-signs">
            {{ $filters.booleanValue(truck.signs, t("general.yes"), t("general.no")) || "-" }}
          </span>
        </div>
      </div>
    </div>
    <div class="box-shadow pa-6 bg-white rounded mb-6">
      <div class="text-grey-darken-3 mb-6 font-weight-medium text-subtitle-1">{{ t("trucks.trailer") }}</div>
      <div class="characteristics-items">
        <div class="d-flex characteristics-item">
          <span class="text-grey-darken-1 characteristics-label">{{ t("trucks.length") }}</span>
          <span class="text-grey-darken-3" data-qa="truck-trailer-length">
            {{ truck.trailer?.length ?? "-" }}
          </span>
        </div>
        <div class="d-flex characteristics-item">
          <span class="text-grey-darken-1 characteristics-label">{{ t("trucks.width") }}</span>
          <span class="text-grey-darken-3" data-qa="truck-trailer-width">
            {{ truck.trailer?.width ?? "-" }}
          </span>
        </div>
        <div class="d-flex characteristics-item">
          <span class="text-grey-darken-1 characteristics-label">{{ t("trucks.door-width") }}</span>
          <span class="text-grey-darken-3" data-qa="truck-trailer-door-width">
            {{ truck.trailer?.doorWidth ?? "-" }}
          </span>
        </div>
        <div class="d-flex characteristics-item">
          <span class="text-grey-darken-1 characteristics-label">{{ t("trucks.min-width") }}</span>
          <span class="text-grey-darken-3" data-qa="truck-trailer-min-width">
            {{ truck.trailer?.minWidth ?? "-" }}
          </span>
        </div>
        <div class="d-flex characteristics-item">
          <span class="text-grey-darken-1 characteristics-label">{{ t("trucks.height") }}</span>
          <span class="text-grey-darken-3" data-qa="truck-trailer-height">
            {{ truck.trailer?.height ?? "-" }}
          </span>
        </div>
        <div class="d-flex characteristics-item">
          <span class="text-grey-darken-1 characteristics-label">{{ t("trucks.door-height") }}</span>
          <span class="text-grey-darken-3" data-qa="truck-trailer-door-height">{{
            truck.trailer?.doorHeight ?? "-"
          }}</span>
        </div>
        <div class="d-flex characteristics-item">
          <span class="text-grey-darken-1 characteristics-label">{{ t("trucks.min-height") }}</span>
          <span class="text-grey-darken-3" data-qa="truck-trailer-min-height">
            {{ truck.trailer?.minHeight ?? "-" }}
          </span>
        </div>
        <div class="d-flex characteristics-item">
          <span class="text-grey-darken-1 characteristics-label">{{ t("trucks.year") }}</span>
          <span class="text-grey-darken-3" data-qa="truck-trailer-year">
            {{ truck.trailer?.year ?? "-" }}
          </span>
        </div>
        <div class="d-flex characteristics-item">
          <span class="text-grey-darken-1 characteristics-label">{{ t("trucks.payload") }}</span>
          <span class="text-grey-darken-3" data-qa="truck-trailer-payload">{{ truck.trailer?.payload }}</span>
        </div>
        <div class="d-flex characteristics-item">
          <span class="text-grey-darken-1 characteristics-label">{{ t("trucks.door-type") }}</span>
          <span class="text-grey-darken-3" data-qa="truck-trailer-door-type">
            {{ doorTypeToString(truck) }}
          </span>
        </div>
        <div class="d-flex characteristics-item">
          <span class="text-grey-darken-1 characteristics-label">{{ t("trucks.equipment") }}</span>
          <span class="text-grey-darken-3" data-qa="truck-trailer-equipment">
            {{ equipmentToString(truck) }}
          </span>
        </div>
        <div class="d-flex characteristics-item">
          <span class="text-grey-darken-1 characteristics-label">{{ t("trucks.equipment-extra") }}</span>
          <span class="text-grey-darken-3" data-qa="truck-trailer-additional-equipment">
            {{ additionalEquipmentToString(truck) }}
          </span>
        </div>
      </div>
    </div>
    <div class="box-shadow pa-6 bg-white rounded mb-2">
      <div class="text-grey-darken-3 mb-6 font-weight-medium text-subtitle-1">{{ t("trucks.truck-note") }}</div>
      <div class="driver-note__text bg-grey-lighten-4 pt-4 pb-4 pr-3 pl-3" style="min-height: 110px">
        {{ $filters.mdash(truck.note || "") }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.box-shadow {
  box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.16);
}

.characteristics-item {
  align-items: center;
  padding: 10px 0;
}

.characteristics-label {
  width: 120px;
  margin-right: 30px;
}

.characteristics-items {
  columns: 2;
  margin: -10px 0;
}
</style>
